/**
 * Repeat-group ("Table Matrix" and cards mode) add/remove/reindex logic.
 *
 * Per .claude/skills/farmer-form-engine-frontend/SKILL.md §4: the prototype
 * (smc-carbon-survey-question-builder.html) only ever removes the *trailing* instance
 * (the "Bớt dòng/bản ghi" button decrements a manual counter and the last DOM row is
 * dropped — see `syncRepeats()` around line 2411 there), and answers for that trailing
 * index are simply left behind in `S` (never deleted). That's fine for a client-only /
 * localStorage app but breaks the API model here: the server merges `answers` with `||`
 * and never deletes keys (Doc/03 §5 — PATCH /submissions/:id/answers), so a stale
 * "resurrected" row is a real bug once autosave is involved.
 *
 * This module implements proper mid-list removal with reindexing, following the skill's
 * documented algorithm verbatim.
 */
import { MAX_REPEAT_INSTANCES } from '@farmer/audit-rules';
import { repeatCount, buildPrefix } from './keys.js';

/**
 * Trần số dòng một nhóm lặp. Lấy từ @farmer/audit-rules chứ không khai báo lại: server
 * dùng đúng hằng số này khi kiểm câu bắt buộc lúc nộp, lệch nhau thì phiếu bị chặn bởi
 * một dòng mà renderer không bao giờ vẽ ra.
 */
export const MAX_INSTANCES = MAX_REPEAT_INSTANCES;

/** How many instances of `repeatName` exist under `parentPrefix` right now. */
export function countInstances(state, repeatName, parentPfx = '') {
  return repeatCount(state.all(), repeatName, parentPfx);
}

/** Append a new (empty) instance. Returns the new instance's 0-based index. */
export function addRepeatRow(state, repeatName, parentPfx = '') {
  const n = countInstances(state, repeatName, parentPfx);
  if (n >= MAX_INSTANCES) return n - 1;
  // Nothing to write — an instance "exists" once any key at that index is set by the
  // renderer/user. We just report the index the caller should render next.
  return n;
}

/**
 * Remove instance `removedIndex` of `repeatName` (under `parentPrefix`) and shift every
 * later instance's keys down by one, including keys belonging to nested repeats under it.
 *
 * Mutates `state` in place (via state.set/state.delete) and returns the list of keys that
 * must be sent as `removeKeys` in the next PATCH — the server never deletes keys on its
 * own, so the client has to say explicitly which ones are gone (skill §4, same warning as
 * above).
 */
export function removeRepeatRow(state, repeatName, removedIndex, parentPfx = '') {
  const all = state.all();
  const n = repeatCount(all, repeatName, parentPfx);
  const removeKeys = [];
  const renames = []; // [oldKey, newKey], applied after collecting to avoid clobbering

  for (let i = removedIndex; i < n; i++) {
    const from = buildPrefix(parentPfx, repeatName, i);
    const keysAtI = Object.keys(all).filter((k) => k.startsWith(from));
    if (i === removedIndex) {
      keysAtI.forEach((k) => removeKeys.push(k));
    } else {
      const to = buildPrefix(parentPfx, repeatName, i - 1);
      keysAtI.forEach((k) => renames.push([k, to + k.slice(from.length)]));
    }
  }

  // Apply: delete the removed row's keys, then shift every later row down by one index.
  removeKeys.forEach((k) => state.delete(k));
  // Renames must apply in ascending index order (already collected that way) so a target
  // key is never overwritten before it's read.
  renames.forEach(([oldKey, newKey]) => {
    const v = all[oldKey];
    state.set(newKey, v);
    state.delete(oldKey);
  });

  return removeKeys;
}

/**
 * Compute the "wanted" instance count for an auto (`repeat_count`) repeat, capped at
 * MAX_INSTANCES, given the already-evaluated repeat_count expression result.
 */
export function clampAutoCount(rawCountStr) {
  const n = parseInt(rawCountStr, 10) || 0;
  return { want: Math.max(0, Math.min(MAX_INSTANCES, n)), raw: n, capped: n > MAX_INSTANCES };
}
